import { Sparkle } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  useParseMenuFromFileMutation,
  useParseMenuFromUrlMutation,
} from "@/api/admin/brands";
import { AdminSections } from "@/api/admin/teamRoles";
import { CustomButton } from "@/components/atoms";
import { toastError } from "@/components/molecules";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { usePermission } from "@/services/casl/usePermission";
import { rootStore } from "@/store/RootStore";
import { formatTestId } from "@/utils/formatTestId";
import { isBrowserOffline, isNetworkError } from "@/utils/isNetworkError";

import ErrorBlock from "../components/ErrorBlock";
import LoaderExtractor from "../components/LoaderExtractor";
import SummaryBar from "../components/SummaryBar";
import { useMenuActions } from "../menuActions/MenuActionsContext";
import {
  isEmptyMenuParseResponse,
  mapMenuParseResponseToCategories,
} from "../menuMappers";
import type { BrandFormMenuType, CategoryType } from "../types";
import { LinkTab } from "./LinkTab";
import { UploadTab } from "./UploadTab";

export type ErrorConfigType = {
  title: string;
  description: string;
  actions: {
    label: string;
    handler: () => void;
    variant?: "light" | "destructive" | "default";
  }[];
};

enum ExtractionError {
  PARSED_FAILED = "parse_failed",
  EMPTY_MENU = "empty_menu",
  NETWORK = "network",
}

interface Props {
  brandId?: string;
  setIsScratch: (value: boolean) => void;
  isExtracted: boolean;
  setIsExtracted: (value: boolean) => void;
  onMenuReset: () => void | Promise<void>;
  isResettingMenu?: boolean;
}

function markDuplicateCategories(categories: CategoryType[]): CategoryType[] {
  const nameCounts = new Map<string, number>();
  for (const c of categories) {
    const key = c.categoryNameEn.toLowerCase();
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
  }

  return categories.map(c => ({
    ...c,
    isDuplicate: (nameCounts.get(c.categoryNameEn.toLowerCase()) ?? 0) > 1,
  }));
}

const TabsMenuConnector = observer(
  ({
    brandId,
    setIsScratch,
    isExtracted,
    setIsExtracted,
    onMenuReset,
    isResettingMenu,
  }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [extractedSourceName, setExtractedSourceName] = useState<
      string | null
    >(null);
    const [extractionError, setExtractionError] =
      useState<ExtractionError | null>(null);

    const hasUpdatePermission = usePermission(AdminSections.BRAND, {
      isUpdate: true,
    });

    const { mutateAsync: parseMenuFromUrl } = useParseMenuFromUrlMutation();
    const { mutateAsync: parseMenuFromFile } = useParseMenuFromFileMutation();
    const menuActions = useMenuActions();

    const {
      watch,
      setValue,
      trigger,
      formState: { errors },
    } = useFormContext<BrandFormMenuType>();

    const file = watch("file");
    const url = watch("url") ?? "";
    const sourceFileName = watch("sourceFileName");
    const sourceMenuUrl = watch("sourceMenuUrl");

    const hasSource = !!watch("sourceFileUrl") || !!watch("sourceMenuUrl");

    const hasValidSource = useMemo(() => {
      const hasFile = !!file;
      const hasUrl = !!url.trim();
      return (hasFile && !errors.file) || (hasUrl && !errors.url);
    }, [file, url, errors.file, errors.url]);

    const getSourceName = () =>
      file?.name?.trim() || url.trim() || sourceFileName || sourceMenuUrl || "";

    const fileName = extractedSourceName ?? getSourceName();

    const persistSourceMetadata = (sourceName: string) => {
      setExtractedSourceName(sourceName);

      if (file?.name?.trim()) {
        setValue("sourceFileName", file.name.trim(), { shouldDirty: true });
        setValue("sourceMenuUrl", undefined, { shouldDirty: true });
      } else if (url.trim()) {
        setValue("sourceMenuUrl", url.trim(), { shouldDirty: true });
        setValue("sourceFileName", undefined, { shouldDirty: true });
      }

      setIsExtracted(true);
      setIsScratch(false);
    };

    const triggerError = (type: ExtractionError) => {
      setIsLoading(false);
      setExtractionError(type);
    };

    const clearError = () => setExtractionError(null);

    const finishExtraction = (categories: CategoryType[]) => {
      setIsLoading(false);

      rootStore.menuStore.initMenu({
        ...rootStore.menuStore.menu,
        categories,
      });

      setValue("categories", categories, { shouldValidate: false });
    };

    const runExtraction = async () => {
      if (!brandId) {
        toastError(
          "Brand ID is required to extract the menu.",
          () => {},
          "Error"
        );
        return;
      }

      if (isBrowserOffline()) {
        triggerError(ExtractionError.NETWORK);
        return;
      }

      setIsLoading(true);
      clearError();

      try {
        const response = file
          ? await parseMenuFromFile({ file })
          : await parseMenuFromUrl({
              url: url.trim(),
            });

        if (isEmptyMenuParseResponse(response)) {
          triggerError(ExtractionError.EMPTY_MENU);
          return;
        }

        const categories = markDuplicateCategories(
          mapMenuParseResponseToCategories(
            response,
            rootStore.menuStore.priceDecimals
          )
        );

        const sourceName = getSourceName();
        persistSourceMetadata(sourceName);

        if (menuActions.isEnabled) {
          const formMenu = await menuActions.persistParsedMenu(categories);
          finishExtraction(formMenu?.categories ?? categories);
        } else {
          finishExtraction(categories);
        }
      } catch (err: unknown) {
        triggerError(
          isNetworkError(err)
            ? ExtractionError.NETWORK
            : ExtractionError.PARSED_FAILED
        );
      }
    };

    const handleManualSubmit = async () => {
      const isValid = await trigger(["file", "url"]);
      if (isValid) {
        await runExtraction();
      }
    };

    const handleRetry = () => runExtraction();

    const handleReupload = () => {
      clearError();
      setExtractedSourceName(null);
      setIsExtracted(false);
      setValue("file", undefined);
      setValue("url", "");
      setValue("sourceFileName", undefined);
      setValue("sourceMenuUrl", undefined);
    };

    const handleBuildFromScratch = () => {
      clearError();
      setIsScratch(true);
    };

    const handleResetMenu = async () => {
      try {
        await onMenuReset();
        setExtractedSourceName(null);
        setIsModalOpen(false);
      } catch (error) {
        toastError(
          error instanceof Error ? error?.message : "Something went wrong!",
          () => handleResetMenu(),
          "Error"
        );
      }
    };

    const tabs = [
      {
        value: "upload",
        title: "Upload a PDF",
        content: (
          <UploadTab
            file={file}
            disabled={!!url.trim()}
            error={errors.file?.message}
            onChange={async selectedFile => {
              setValue("file", selectedFile, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (selectedFile) {
                setValue("url", "");
              }
              await trigger(["file", "url"]);
            }}
          />
        ),
      },
      {
        value: "link",
        title: "Paste a link",
        content: (
          <LinkTab
            value={url}
            disabled={!!file}
            error={errors.url?.message}
            onChange={async value => {
              setValue("url", value, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (value.trim()) {
                setValue("file", undefined);
              }
              await trigger(["file", "url"]);
            }}
          />
        ),
      },
    ];

    const ERROR_CONFIG: Record<ExtractionError, ErrorConfigType> = {
      [ExtractionError.PARSED_FAILED]: {
        title: "Couldn't read your menu",
        description:
          "The file appears to be damaged, unreadable, or doesn't contain a text layer. Try a different file or build the menu manually.",
        actions: [
          { label: "Retry", handler: handleRetry },
          { label: "Re-upload", handler: handleReupload, variant: "light" },
          {
            label: "Build from scratch",
            handler: handleBuildFromScratch,
            variant: "light",
          },
        ],
      },
      [ExtractionError.EMPTY_MENU]: {
        title: "Couldn't fetch menu",
        description:
          "Our AI couldn't identify any categories or dishes in this file. You can try again or build the menu manually.",
        actions: [
          { label: "Retry", handler: handleRetry },
          { label: "Cancel", handler: handleReupload, variant: "light" },
        ],
      },
      [ExtractionError.NETWORK]: {
        title: "Connection error",
        description:
          "Something went wrong while reaching the server. Check your connection and try again.",
        actions: [
          { label: "Retry", handler: handleRetry },
          { label: "Re-upload", handler: handleReupload, variant: "light" },
          {
            label: "Build from scratch",
            handler: handleBuildFromScratch,
            variant: "light",
          },
        ],
      },
    };

    if (isLoading) {
      return <LoaderExtractor fileName={fileName} />;
    }

    if (extractionError) {
      return <ErrorBlock cfg={ERROR_CONFIG[extractionError]} />;
    }

    if (isExtracted || hasSource) {
      return (
        <SummaryBar
          fileName={fileName}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          handleResetMenu={handleResetMenu}
          isResettingMenu={isResettingMenu}
        />
      );
    }

    return (
      <>
        <Tabs
          defaultValue="upload"
          data-testid="tabs-navigation-wrapper"
          className="w-full"
        >
          <TabsList
            className={cn("inline-flex h-12 rounded-md bg-[#FFF4D7] p-1")}
          >
            {tabs.map(elem => (
              <TabsTrigger
                key={elem.value}
                value={elem.value}
                data-testid={`tabs-trigger-${formatTestId(elem.value)}`}
                disabled={!hasUpdatePermission}
                className={cn(
                  "h-full px-8 rounded-md font-bold transition-all duration-200 cursor-pointer text-gray-500 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-xs disabled:pointer-events-none disabled:opacity-50"
                )}
              >
                {elem.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map(elem => (
            <TabsContent key={elem.value} value={elem.value} className="mt-6">
              {elem.content}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-8 flex justify-center">
          <CustomButton
            type="button"
            onClick={handleManualSubmit}
            disabled={!hasValidSource || !brandId}
            className="!px-10"
          >
            <Sparkle fill="#000000" strokeWidth={1} />
            <span>Extract menu with AI</span>
          </CustomButton>
        </div>

        <div className="relative my-10">
          <div className="border-t" />
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-gray-500">
            or
          </span>
        </div>

        <div className="border rounded-2xl p-8 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold">Build it from scratch</h3>
            <p className="text-gray-500 mt-2 text-sm">
              Start with an empty menu and add categories and dishes yourself.
            </p>
          </div>
          <CustomButton
            variant="light"
            size="lg"
            onClick={() => setIsScratch(true)}
            disabled={!hasUpdatePermission}
          >
            Start from scratch
          </CustomButton>
        </div>
      </>
    );
  }
);

export default TabsMenuConnector;
